<template>
  <div class="field">
    <FloorLevelTabs
      v-model:selected-floor-level="selectedFloorLevel"
      :device-groups="deviceGroups"
    />

    <DeviceGroupList
      :device-groups="deviceGroups"
      :arm-installation-points="armInstallationPoints"
      :update-arm-installation-points="updateArmInstallationPoints"
      :device="device"
      :selected-floor-level="selectedFloorLevel"
      :installation-point="installationPoint"
    />
  </div>
</template>

<script setup lang="ts">
import { useRoomStore } from '@/store/entities/roomStore';
import { useEntitiesStore } from '@/store/entities/entitiesStore';
import { useRoomGroupStore } from '@/store/entities/roomGroupStore';
import { useDeviceStore } from '@/store/entities/deviceStore';
import { useBuildingStore } from '@/store/entities/buildingStore';
import { DeviceClasses } from '@/models/devices/Device';
import { InstallationPoint } from '@/models/installationPoint/InstallationPoint';
import { computed, onMounted, ref, WritableComputedRef } from 'vue';
import FloorLevelTabs from '@/components/Forms/DeviceFormComponents/AdvancedRegistraionMeter/FloorLevelTabs.vue';
import DeviceGroupList from '@/components/Forms/DeviceFormComponents/AdvancedRegistraionMeter/DeviceGroupList.vue';
import { useInstallationPointStore } from '@/store/entities/installationPointStore';

const entitiesStore = useEntitiesStore();
const roomGroupStore = useRoomGroupStore();
const roomStore = useRoomStore();
const deviceStore = useDeviceStore();
const buildingStore = useBuildingStore();

const props = defineProps<{
  device: DeviceClasses;
  installationPoint: InstallationPoint;
  armInstallationPoints?: InstallationPoint[];
  updateArmInstallationPoints?: (
    newArmInstallationPoints: InstallationPoint[]
  ) => void;
}>();
const emits = defineEmits(['update:device', 'update:installationPoint']);

const selectedFloorLevel = ref<string>('');

onMounted(() => {
  const setDefaultFloor = () => {
    const activeDeviceGroup = deviceGroups.value?.filter(
      (group) => group.id === localInstallationPoint.value.billedInRoomGroupId
    )[0];

    if (activeDeviceGroup) {
      selectedFloorLevel.value = activeDeviceGroup.name;
    }
  };

  const setDefaultArmFloor = () => {
    const armDevices =
      useInstallationPointStore().getAllInstallationPointsByArmId(
        localDevice.value.installationPointId
      );

    props.updateArmInstallationPoints(armDevices);

    if (armDevices.length > 0) {
      const armDevicesFloor = deviceGroups.value.find(
        (group) => group.id === armDevices[0].billedInRoomGroupId
      );
      if (armDevicesFloor) {
        selectedFloorLevel.value = armDevicesFloor.name;
      }
    }
  };

  if (localInstallationPoint.value.isAdvancedRegistrationMeter) {
    setDefaultArmFloor();
  } else {
    setDefaultFloor();
  }
});

const localDevice: WritableComputedRef<DeviceClasses> = computed({
  get() {
    return props.device;
  },
  set(value) {
    emits('update:device', value);
  },
});

const localInstallationPoint: WritableComputedRef<InstallationPoint> = computed(
  {
    get() {
      return props.installationPoint;
    },
    set(value) {
      emits('update:installationPoint', value);
    },
  }
);

export type DeviceGroup = {
  name: string;
  label: string;
  id: string;
};
const deviceGroups: WritableComputedRef<DeviceGroup[]> = computed(() => {
  if (!buildingStore.buildingId) {
    throw new Error('No building id set');
  }
  const building = entitiesStore.getEntityById(buildingStore.buildingId);
  const roomGroups = roomGroupStore.getRoomGroupsByBuildingId(
    buildingStore.buildingId
  );

  return roomGroups.map((group) => {
    return {
      name: `(${roomStore.getFirstRoomOfRoomgroup(group.id)?.getFloor()})`,
      label: `${group.getLabel()} - ${building.getLabel()}`,
      id: group.id,
    };
  });
});
</script>
